/** Every document coordinate is expressed in pt on a 1125 × 810 pt page. */
export const PAGE_W = 1125;
export const PAGE_H = 810;
export const pt = (n) => `${n}pt`;
export const INK = "#131414"; // primary dark used across the template
export const BODY_INK = "#231f20"; // paragraph colour
export function Page({ children, background = "#ffffff", id }) {
    return (<section className="doc-page" dir="rtl" id={id} style={{ background }}>
      {children}
    </section>);
}
/** A single absolutely positioned line of text, placed from the reference geometry. */
export function Text({ children, top, size, weight = 400, color = INK, right, left, center, width, justify, wrap, dir = "rtl", style }) {
    const s = {
        position: "absolute",
        top: pt(top),
        fontSize: pt(size),
        fontWeight: weight,
        color,
        whiteSpace: wrap || justify ? "normal" : "nowrap",
        ...style,
    };
    if (right !== undefined)
        s.right = pt(PAGE_W - right);
    if (left !== undefined)
        s.left = pt(left);
    if (center !== undefined) {
        s.left = pt(center);
        s.transform = "translateX(-50%)";
    }
    if (width !== undefined)
        s.width = pt(width);
    if (justify) {
        s.textAlign = "justify";
        s.textAlignLast = "justify";
    }
    else if (width !== undefined) {
        s.textAlign = dir === "rtl" ? "right" : "left";
    }
    return (<div className="doc-text" dir={dir} style={s}>
      {children}
    </div>);
}
/**
 * The Artika mark, traced 1:1 from the vector paths in the reference PDF
 * (square ring + inverted-L frame). Positioned by its top-left in pt.
 */
const MARK_W = 132.858;
const MARK_H = 134.34;
export function LogoMark({ x, y, w, color = INK, opacity = 1 }) {
    const h = (w * MARK_H) / MARK_W;
    return (<svg className="doc-logo" viewBox={`0 0 ${MARK_W} ${MARK_H}`} style={{ position: "absolute", left: pt(x), top: pt(y), width: pt(w), height: pt(h), opacity }} aria-label="Artika">
      <path fill={color} fillRule="evenodd" d="M0 38.774H95.224V134.34H0Z M20.813 59.586V113.527H74.469V59.586Z"/>
      <path fill={color} d="M0 0V24.918H108.054V134.34H132.858V0Z"/>
    </svg>);
}
/** Logo positioned by centre — convenient for values measured from the reference. */
export function LogoAt({ cx, cy, w, color, opacity }) {
    const h = (w * MARK_H) / MARK_W;
    return <LogoMark x={cx - w / 2} y={cy - h / 2} w={w} color={color} opacity={opacity}/>;
}
/** The «ارتيكا» wordmark used in the top-right corner of template pages. */
export function Brand({ right, top, size, color = INK }) {
    return (<Text right={right} top={top} size={size} weight={500} color={color}>
      ارتيكا
    </Text>);
}
/** A fixed template image placed at reference coordinates. */
export function Img({ src, x, y, w, h, opacity, style }) {
    return (<img src={src} alt="" draggable={false} className="doc-img" style={{ position: "absolute", left: pt(x), top: pt(y), width: pt(w), height: pt(h), opacity, ...style }}/>);
}
export function Box({ x, y, w, h, style, children, className }) {
    return (<div className={className} style={{ position: "absolute", left: pt(x), top: pt(y), width: pt(w), height: pt(h), ...style }}>
      {children}
    </div>);
}
/**
 * An uploaded image cover-cropped into a fixed frame (w × h pt).
 * The crop never distorts: the image keeps its ratio, is scaled to cover
 * the frame × zoom, and is shifted according to the focus point.
 */
export function CroppedImage({ image, crop, w, h }) {
    const base = Math.max(w / image.width, h / image.height);
    const dw = image.width * base * crop.zoom;
    const dh = image.height * base * crop.zoom;
    const left = (w - dw) * crop.x;
    const top = (h - dh) * crop.y;
    return (<img src={image.src} alt="" draggable={false} className="doc-img" style={{ position: "absolute", left: pt(left), top: pt(top), width: pt(dw), height: pt(dh), maxWidth: "none" }}/>);
}
